import { type RcFile } from 'antd/lib/upload';
import {
  countryCodes,
  FILE_UPLOAD_MAX_SIZE,
  FILE_UPLOAD_MAX_SIZE_MB,
  FILENAME_MAX_LENGTH,
  SUFFIX_LIST,
} from 'src/constants';
import { FilenameExceededError, FileSizeExceededError, NotSupportedFileError } from 'src/errors';
import { mask } from 'src/utils';
import * as Yup from 'yup';

export const formatValidationSchemaError = (
  error: Yup.ValidationError | any,
): Record<string, string | string[]> | any => {
  if (error instanceof Yup.ValidationError) {
    return error.inner.reduce((acc: any, error: any) => {
      acc[error.path] = error.errors;

      return acc;
    }, {});
  }

  return error;
};

export const suffixValidator = Yup.string()
  .trim()
  .max(4, 'Suffix can have a maximum length of 4 characters')
  .test(
    'suffix',
    'Suffix must be one of "Jr", "Sr", "I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX"',
    function (value: string | any) {
      if (!value) {
        return true;
      }
      let isSufixFound = false;
      SUFFIX_LIST.forEach(suffix => {
        if (suffix.toLowerCase() === value?.toLowerCase()) {
          isSufixFound = true;
        }
      });

      return isSufixFound;
    },
  );

export const phoneNumberValidator = Yup.array()
  .of(
    Yup.object({
      prefix: Yup.string().required('Phone Number country code is required'),
      value: Yup.string()
        .required('Phone Number is required')
        .test('compare', 'Phone Number is invalid', (value: any, schema: any) => {
          if (isNaN(Number(value))) {
            return false;
          }
          const country = countryCodes.find(anItem => anItem.dialCode === schema?.parent?.prefix);
          const masked = mask({ value, mask: country?.phoneMask ?? '', substitute: '9' });

          return masked?.length === country?.phoneMask?.length;
        }),
      type: Yup.string().required('Phone Number type is required'),
    }),
  )
  .required('At least a valid Phone Number is required');

export const validateFile = (
  file: RcFile,
  {
    accept = ['image/png', 'image/jpeg', 'application/pdf'],
    maxFileSize = FILE_UPLOAD_MAX_SIZE,
  }: { accept?: string[]; maxFileSize?: number } = {},
) => {
  const fileSizeRatioToMaxFileSize = (file.size / maxFileSize).toFixed(2);
  const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
  const isSupportedFileSize = Number(fileSizeRatioToMaxFileSize) <= 1;
  const isSupportedFileType = accept.includes(file.type);
  const fileName = file.name;

  if (!isSupportedFileSize) {
    return Promise.reject(new FileSizeExceededError({ maximum: FILE_UPLOAD_MAX_SIZE_MB, actual: Number(fileSizeMB) }));
  }

  if (!isSupportedFileType) {
    return Promise.reject(new NotSupportedFileError({ allowed: accept, actual: file.type }));
  }

  if (fileName.length > FILENAME_MAX_LENGTH) {
    return Promise.reject(new FilenameExceededError({ maximum: FILENAME_MAX_LENGTH, actual: fileName.length }));
  }

  return;
};
